import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Language } from '../../../constants/language';
import { ministryService } from '../../../services/ministry-service';
import { Ministry } from '../../../models/ministry/ministry';
import { usePageSEO } from '../../../hooks';

const Ministries: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the ministries page
  usePageSEO('ministries');

  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const [ministriesArray, setMinistriesArray] = useState<Ministry[]>([]);

  useEffect(() => {

    ministryService.getMinistries(i18n.language as Language).then(result => {
      setMinistriesArray(result.data);
    });
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%)]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                Discover Your Purpose
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                {t('pages.ministry.title', 'Zoe ministry is divided into various sectors')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300">
                </span>
              </h1>
              
              <p className="text-lg text-gray-300 leading-relaxed mb-8 max-w-lg mx-auto text-center">
                {t('pages.ministry.description', 'Each sector serves a specific need in people\'s lives, leading them to God\'s presence and the touch of the Holy Spirit.')}
              </p>

              <div className="flex justify-center">
                <button 
                  onClick={() => document.querySelector('#ministries-grid')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-black hover:bg-gray-100 px-6 py-2.5 text-sm font-semibold transition-colors duration-300 rounded-lg flex items-center justify-center space-x-2"
                >
                  <span>{t('pages.ministry.exploreMinistries', 'Explore Ministries')}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Modern Ministries Grid */}
      <section id="ministries-grid" className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              {t('pages.ministry.ourMinistries', 'Our Ministries')}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('pages.ministry.brief', 'Each ministry is designed to meet you where you are and help you grow in your faith journey')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {ministriesArray.map((ministry) => (
              <div 
                key={ministry.id} 
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-gray-300 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
              >
                {/* Ministry Image & Header */}
                <div className="relative h-48 bg-gradient-to-br from-gray-800 to-black overflow-hidden">
                  <div className="absolute inset-0 bg-black/20"></div>
                  
                  {/* Header with flexbox layout */}
                  <div className="relative h-full p-6 flex flex-col justify-between">
                    {/* Top row: Category Badge only */}
                    <div className="flex justify-end">
                      {/* Category badge */}
                      <span className="px-3 py-1 bg-black/60 backdrop-blur-sm text-white text-xs font-medium rounded-full border border-white/20">
                        {ministry.category || 'Ministry'}
                      </span>
                    </div>

                    {/* Bottom section: Icon next to Title and Time */}
                    <div className="mt-auto">
                      <div className="flex items-center space-x-3 mb-2">
                        {/* Icon */}
                        <div className="w-10 h-10 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                          <img
                            src={ministry.media.logo.url}
                            alt={ministry.title}
                            className="w-6 h-6 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<div class="w-6 h-6 bg-black/20 rounded flex items-center justify-center text-black text-xs font-bold">${ministry.title.charAt(0)}</div>`;
                              }
                            }}
                          />
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-white leading-tight flex-1">
                          {ministry.title}
                        </h3>
                      </div>

                      {/* Time - below the icon and title */}
                      {ministry.time && (
                        <p className="text-white/90 text-sm ml-13">
                          {ministry.time}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Ministry Content */}
                <div className="p-8 flex flex-col h-full">
                  {/* Content that can expand */}
                  <div className="flex-grow">
                    {/* Description */}
                    <div className="mb-6">
                      <p className="text-gray-600 leading-relaxed text-base">
                        {ministry.description}
                      </p>
                    </div>

                    {/* Quick Info */}
                    {ministry.address && (
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{ministry.address.name}</span>
                      </div>
                    )}

                    {/* Key Features - Show top 2 */}
                    {ministry.includes && (
                      <div className="mb-6">
                        <div className="grid grid-cols-1 gap-2">
                          {ministry.includes.slice(0, 2).map((feature: string, featureIndex: number) => (
                            <div key={featureIndex} className="flex items-start">
                              <div className="w-1.5 h-1.5 bg-black rounded-full mt-2.5 mr-3 flex-shrink-0"></div>
                              <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                            </div>
                          ))}
                          {ministry.includes.length > 2 && (
                            <div className="text-xs text-gray-400 ml-5 mt-1">
                              +{ministry.includes.length - 2} {t('general.actions.readMore', 'more')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Button - Always at bottom */}
                  <div className="mt-auto">
                    <Link
                      to={`/ministries/${ministry.id}`}
                      className="w-full bg-black text-white hover:bg-gray-800 px-4 py-2.5 text-sm font-medium transition-colors duration-300 flex items-center justify-center space-x-2 rounded-lg"
                    >
                      <span>{t('general.actions.learnMore')}</span>
                    </Link>
                  </div>
                </div>


              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default Ministries;
